import { useState, useRef, useEffect } from 'react';
import {
  Flex,
  Input,
  Button,
  Icon,
  toast,
  FieldGroup,
  Field,
  FieldTitle,
  FieldDescription,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';
import { motion, AnimatePresence } from 'motion/react';
import { EmailFilters } from './EmailFilters';
import type { IEmailConfigureStepProps } from './types';

/**
 * EmailConfigureStep Component
 *
 * The first step of the email trigger dialog where users see
 * the mailbox address and configure email filters.
 *
 * Contains two sections:
 * 1. Mailbox Address - Read-only display with copy functionality
 * 2. Email Filters - Collapsible filter configuration
 *
 * @param props - Component props
 * @returns JSX element containing the configuration form
 */
export function EmailConfigureStep({
  mailboxAddress,
  filters,
  onFiltersChange,
  readOnly = false,
}: IEmailConfigureStepProps) {
  const { t } = useTranslation('automation-inputs');
  const [, copyToClipboard] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Copies the mailbox address to clipboard and shows success animation
   */
  const handleCopyAddress = async () => {
    const success = await copyToClipboard(mailboxAddress);
    if (success) {
      // Clear any existing timeout
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      setIsCopied(true);
      toast.success(t('emailDialog.mailbox.copied'));
      // Reset after 2 seconds
      copyTimeoutRef.current = setTimeout(() => {
        setIsCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    } else {
      toast.error(t('emailDialog.mailbox.copyError'));
    }
  };

  // eslint-disable-next-line no-restricted-syntax -- reason: Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Flex
      direction="column"
      gap={4}
      width="full"
      className="bg-secondary overflow-y-auto rounded-lg p-5 max-h-[500px]"
    >
      {/* Mailbox Address Section */}
      <FieldGroup className="p-5 bg-card rounded-lg">
        {/* Mailbox Address Input with Copy Button */}
        <Field>
          <FieldTitle>{t('emailDialog.mailbox.title')}</FieldTitle>
          <FieldDescription>
            {t('emailDialog.mailbox.description')}
          </FieldDescription>
          <Flex direction="row" gap={2} align="center" width="full">
            <Input
              type="text"
              value={mailboxAddress}
              readOnly
              className="bg-background flex-1"
              autoFocus={false}
              tabIndex={-1}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyAddress}
              title={t('emailDialog.mailbox.copyTooltip')}
            >
              <AnimatePresence mode="wait" initial={false}>
                {isCopied ? (
                  <motion.span
                    key="check"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <Icon type="Check" size="sm" className="text-success" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="copy"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <Icon type="Copy" size="sm" />
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </Flex>
        </Field>
      </FieldGroup>

      {/* Email Filters Section */}
      <EmailFilters
        filters={filters}
        onFiltersChange={onFiltersChange}
        readOnly={readOnly}
      />
    </Flex>
  );
}
